import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { storesAPI, spaceTypesAPI, blocksAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Lock, Unlock, Plus, Trash2, ChevronLeft, ChevronRight, Building2, Package, AlertTriangle } from 'lucide-react';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export default function AvailabilityBlocksPage() {
  const [searchParams] = useSearchParams();
  const initialStoreId = searchParams.get('store') || '';
  
  const [stores, setStores] = useState([]);
  const [spaceTypes, setSpaceTypes] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // Filters - use initial store from URL if provided
  const [selectedStore, setSelectedStore] = useState(initialStoreId);
  const [selectedSpaceType, setSelectedSpaceType] = useState('');
  
  // Calendar view
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState([]);
  
  // New block form
  const [showForm, setShowForm] = useState(false);
  const [newBlock, setNewBlock] = useState({
    store_id: initialStoreId,
    space_type_id: '',
    start_month: '',
    end_month: '',
    reason: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedStore && selectedSpaceType) {
      loadCalendar();
      loadBlocks();
    }
  }, [selectedStore, selectedSpaceType, currentYear]);

  const loadInitialData = async () => {
    try {
      const [storesRes, spaceTypesRes] = await Promise.all([
        storesAPI.getAll(),
        spaceTypesAPI.getAll()
      ]);
      setStores(storesRes.data);
      setSpaceTypes(spaceTypesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadBlocks = async () => {
    try {
      const response = await blocksAPI.getAll(selectedStore, selectedSpaceType);
      setBlocks(response.data);
    } catch (error) {
      console.error('Error loading blocks:', error);
    }
  };

  const loadCalendar = async () => {
    try {
      const startMonth = `${currentYear}-01`;
      const endMonth = `${currentYear}-12`;
      const response = await blocksAPI.getCalendar(selectedStore, selectedSpaceType, startMonth, endMonth);
      setCalendarData(response.data.months);
    } catch (error) {
      console.error('Error loading calendar:', error);
    }
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    if (!newBlock.store_id || !newBlock.space_type_id || !newBlock.start_month || !newBlock.end_month) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    setCreating(true);
    try {
      await blocksAPI.create(newBlock);
      toast.success('Bloqueo creado exitosamente');
      setShowForm(false);
      setNewBlock({ store_id: '', space_type_id: '', start_month: '', end_month: '', reason: '' });
      loadBlocks();
      loadCalendar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear bloqueo');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!confirm('¿Estás seguro de eliminar este bloqueo?')) return;
    
    try {
      await blocksAPI.delete(blockId);
      toast.success('Bloqueo eliminado');
      loadBlocks();
      loadCalendar();
    } catch (error) {
      toast.error('Error al eliminar bloqueo');
    }
  };

  const getMonthStatus = (monthData) => {
    if (!monthData) return 'unknown';
    if (monthData.is_blocked) return 'blocked';
    if (monthData.available === 0) return 'full';
    if (monthData.available <= 2) return 'limited';
    return 'available';
  };

  const currentDate = new Date();
  const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="availability-blocks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Lock className="w-6 h-6 text-primary" />
            Gestión de Disponibilidad
          </h1>
          <p className="text-slate-500">Bloquea períodos de disponibilidad para tiendas y productos (estilo Airbnb)</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-block-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Bloqueo
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Seleccionar Tienda y Producto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Tienda</Label>
              <Select value={selectedStore} onValueChange={setSelectedStore}>
                <SelectTrigger data-testid="store-select">
                  <SelectValue placeholder="Selecciona una tienda" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name} - {store.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Producto/Espacio</Label>
              <Select value={selectedSpaceType} onValueChange={setSelectedSpaceType}>
                <SelectTrigger data-testid="space-type-select">
                  <SelectValue placeholder="Selecciona un producto" />
                </SelectTrigger>
                <SelectContent>
                  {spaceTypes.map(st => (
                    <SelectItem key={st.id} value={st.id}>
                      {st.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar View */}
      {selectedStore && selectedSpaceType && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Calendario de Disponibilidad
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentYear(y => y - 1)}
                  disabled={currentYear <= currentDate.getFullYear()}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-semibold text-lg w-16 text-center">{currentYear}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentYear(y => y + 1)}
                  disabled={currentYear >= currentDate.getFullYear() + 2}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
              {MONTHS.map((monthName, index) => {
                const monthStr = `${currentYear}-${String(index + 1).padStart(2, '0')}`;
                const monthData = calendarData.find(m => m.month === monthStr);
                const status = getMonthStatus(monthData);
                const isPast = monthStr < currentMonth;
                
                return (
                  <div
                    key={monthStr}
                    data-testid={`calendar-month-${monthStr}`}
                    className={`
                      p-4 rounded-lg border-2 transition-all text-center
                      ${isPast ? 'opacity-50' : ''}
                      ${status === 'blocked' ? 'bg-red-100 border-red-400' : ''}
                      ${status === 'full' ? 'bg-slate-100 border-slate-300' : ''}
                      ${status === 'limited' ? 'bg-amber-50 border-amber-300' : ''}
                      ${status === 'available' ? 'bg-emerald-50 border-emerald-300' : ''}
                    `}
                  >
                    <div className="font-semibold text-sm">{monthName}</div>
                    {monthData && (
                      <>
                        {status === 'blocked' ? (
                          <div className="mt-1">
                            <Lock className="w-4 h-4 mx-auto text-red-600" />
                            <span className="text-xs text-red-600">Bloqueado</span>
                          </div>
                        ) : (
                          <div className="text-xs mt-1 text-slate-600">
                            Disp: {monthData.available}/{monthData.capacity}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-400"></div>
                <span className="text-xs">Bloqueado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-slate-100 border-2 border-slate-300"></div>
                <span className="text-xs">Sin disponibilidad</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-50 border-2 border-amber-300"></div>
                <span className="text-xs">Limitado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-emerald-50 border-2 border-emerald-300"></div>
                <span className="text-xs">Disponible</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Blocks List */}
      {blocks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Bloqueos Activos ({blocks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {blocks.map(block => (
                <div 
                  key={block.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border"
                  data-testid={`block-${block.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Lock className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {block.store_name}
                        <span className="text-slate-400">•</span>
                        <Package className="w-4 h-4 text-slate-400" />
                        {block.space_type_name}
                      </div>
                      <div className="text-sm text-slate-500">
                        {block.start_month} → {block.end_month}
                        {block.reason && <span className="ml-2 text-slate-400">({block.reason})</span>}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDeleteBlock(block.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-block-${block.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Block Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Crear Nuevo Bloqueo
              </CardTitle>
              <CardDescription>
                Bloquea un período de disponibilidad para una tienda y producto específico
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateBlock} className="space-y-4">
                <div>
                  <Label>Tienda *</Label>
                  <Select 
                    value={newBlock.store_id} 
                    onValueChange={(v) => setNewBlock({...newBlock, store_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tienda" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name} - {store.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Producto *</Label>
                  <Select 
                    value={newBlock.space_type_id} 
                    onValueChange={(v) => setNewBlock({...newBlock, space_type_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaceTypes.map(st => (
                        <SelectItem key={st.id} value={st.id}>
                          {st.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Mes Inicio *</Label>
                    <Input 
                      type="month" 
                      value={newBlock.start_month}
                      onChange={(e) => setNewBlock({...newBlock, start_month: e.target.value})}
                      min={currentMonth}
                    />
                  </div>
                  <div>
                    <Label>Mes Fin *</Label>
                    <Input 
                      type="month" 
                      value={newBlock.end_month}
                      onChange={(e) => setNewBlock({...newBlock, end_month: e.target.value})}
                      min={newBlock.start_month || currentMonth}
                    />
                  </div>
                </div>

                <div>
                  <Label>Razón del Bloqueo (opcional)</Label>
                  <Textarea 
                    value={newBlock.reason}
                    onChange={(e) => setNewBlock({...newBlock, reason: e.target.value})}
                    placeholder="Ej: Mantenimiento programado, Renovación de tienda..."
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? 'Creando...' : 'Crear Bloqueo'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
